// plan-11 S06: install affordance + iOS hint — bible §39

import { load, set } from './save'
import { play } from '../audio/sfx'

let deferredPrompt: BeforeInstallPromptEvent | null = null
let chipEl: HTMLElement | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

export function initInstall(): void {
  const s = load()
  const query = new URLSearchParams(window.location.search)
  const isPwa = query.get('pwa') === '1'

  // PWA installed mode: suppress install chip
  if (isPwa) {
    document.body.classList.add('vt-pwa-installed')
    set('pwaInstalled', true)
    return
  }

  // already installed
  if (s.pwaInstalled) return

  // listen for beforeinstallprompt
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    maybeShowChip()
  })

  // listen for appinstalled
  window.addEventListener('appinstalled', () => {
    set('pwaInstalled', true)
    hideChip()
    // stream a line into terminal
    const history = document.querySelector('.vt-history') as HTMLElement | null
    if (history) {
      const line = document.createElement('div')
      line.className = 'vt-line-oracle'
      line.style.color = 'var(--vt-king-green)'
      line.textContent = '> install: confirmed. oracle is local.'
      history.appendChild(line)
    }
  })

  // iOS hint: one-shot on first visit
  const ua = navigator.userAgent
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !('standalone' in navigator)
  if (isIOS && s.visits === 0) {
    // show one-shot hint after boot
    setTimeout(() => {
      const history = document.querySelector('.vt-history') as HTMLElement | null
      if (history) {
        const line = document.createElement('div')
        line.className = 'vt-line-oracle'
        line.style.color = 'var(--vt-green-dim)'
        line.textContent = '// to install on ios: share menu -> add to home screen.'
        history.appendChild(line)
      }
      set('installPromptDismissed', Date.now())
    }, 4000)
  }
}

function maybeShowChip(): void {
  const s = load()
  // check cooldown
  if (Date.now() - s.installPromptDismissed < COOLDOWN_MS && s.installPromptDismissed > 0) return
  if (s.pwaInstalled) return

  showChip()
}

function showChip(): void {
  if (chipEl) return

  const row = document.getElementById('vt-sb-chip-row')
  if (!row) return

  chipEl = document.createElement('span')
  chipEl.id = 'vt-install-chip'
  chipEl.className = 'vt-sb-chip'
  chipEl.textContent = '[ ◇ install oracle ]'
  chipEl.setAttribute('role', 'button')
  chipEl.setAttribute('tabindex', '0')
  chipEl.setAttribute('aria-label', 'install oracle as app')
  chipEl.style.cursor = 'pointer'
  chipEl.addEventListener('click', handleInstallClick)
  chipEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleInstallClick()
    }
  })
  row.appendChild(chipEl)
}

function hideChip(): void {
  if (chipEl) {
    chipEl.remove()
    chipEl = null
  }
}

async function handleInstallClick(): Promise<void> {
  if (!deferredPrompt) return

  try {
    const result = await deferredPrompt.prompt()
    if (result.outcome === 'accepted') {
      play('command_accept')
    } else {
      // dismissed — set cooldown
      set('installPromptDismissed', Date.now())
      hideChip()
    }
    deferredPrompt = null
  } catch {
    // prompt failed silently
  }
}
